import { Tabs, TabsContent, TabsList, TabsTrigger } from '@natu/ui/Tabs';
import { cn } from '@natu/utils/cn';
import { isArrayWithLength } from '@natu/utils/isArrayWithLength';

import {
  type BlokItem,
  DynamicRender,
  resolveStoryblokStyles,
  sbEditable,
  type SBProps,
  type Spacing,
} from '../../../../utils';
import { FaqSchemaFromServicesList } from './FaqSchemaFromServicesList';

interface ItemProp extends BlokItem<'serviceCardItem'> {
  title?: string;
  subtitle?: string;
  body?: string;
  cta?: BlokItem[];
}

interface SBSupportSectionProps {
  items: ItemProp[];
  mtMobile?: Spacing;
  mtTablet?: Spacing;
  mtDesktop?: Spacing;
  mbMobile?: Spacing;
  mbTablet?: Spacing;
  mbDesktop?: Spacing;
  sendEvent?: boolean;
  event_solution_name?: string;
  generateFaqSchema?: boolean;
}

const addLeadingZero = (num: number) => (num < 10 ? `0${num}` : num.toString());

const getSupportItemData = ({ items }: { items: ItemProp[] }) =>
  items?.filter(item => item.component === 'serviceCardItem');

export const SBServicesList = ({ blok }: SBProps<SBSupportSectionProps>) => {
  const {
    items,
    mbMobile,
    mbTablet,
    mbDesktop,
    mtMobile,
    mtTablet,
    mtDesktop,
    sendEvent,
    event_solution_name,
    generateFaqSchema,
  } = blok;

  const className = resolveStoryblokStyles({
    mt: mtMobile,
    mtTablet,
    mtDesktop,
    mb: mbMobile,
    mbTablet,
    mbDesktop,
  });

  const data = getSupportItemData({ items });

  if (!isArrayWithLength(data)) return null;

  return (
    <div className={cn(className, 'flex flex-col gap-y-10')} {...sbEditable(blok)}>
      <div className="grid gap-12 lg:hidden">
        {items.map((item, index) => (
          <article key={item._uid} className="group">
            <div className="mb-3 flex items-center gap-4">
              <p className="group-first:text-primary-600 text-naturaily-airborne text-2xl">
                {addLeadingZero(index + 1)}
              </p>
              <span className="group-first:bg-primary-600 bg-naturaily-airborne h-[1px] w-full" />
            </div>
            <h3 className="mb-6 text-xl font-semibold">{item.title}</h3>
            <DynamicRender data={item} />
          </article>
        ))}
      </div>

      <Tabs defaultValue={items[0]._uid} className="hidden w-full gap-x-16 lg:flex xl:gap-x-32">
        <TabsList className="flex w-full max-w-[489px] flex-col gap-y-3 rounded-xl p-8 shadow-2xl">
          {data.map(item => (
            <h3 key={item._uid} className="block w-full">
              <TabsTrigger
                value={item._uid}
                className="data-[state=active]:border-l-primary-600 w-full items-center justify-start gap-x-6 border-b-0 border-l-4 border-l-gray-100 bg-[unset] px-4 py-4 pl-6 text-lg font-semibold text-gray-500 data-[state=active]:border-b-0 data-[state=active]:bg-[unset] data-[state=active]:text-gray-900 lg:whitespace-normal lg:text-left"
                event={sendEvent ? 'E_solutions_expand' : undefined}
                eventProps={{ event_solution_name, text: item.title }}
              >
                {item.title}
              </TabsTrigger>
            </h3>
          ))}
        </TabsList>
        <div className="w-full py-4">
          {data.map(item => (
            <TabsContent key={item._uid} value={item._uid}>
              <DynamicRender data={item} />
            </TabsContent>
          ))}
        </div>
      </Tabs>
      {generateFaqSchema && <FaqSchemaFromServicesList items={items} />}
    </div>
  );
};
